import { AlertTriangle, ArrowLeftRight, CheckCircle2, Clock, RefreshCw } from 'lucide-react'

import { Button } from '@krp/ui-kit'
import type { ReactNode } from 'react'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import { ReceiptsBrowser } from './ReceiptsBrowser'
import type { AttentionVm, ParkedVm, ReceiptsActions, ReceiptsTab, ReceiptsVm } from '../model/types'
import './receipts.css'

const TABS: readonly { id: ReceiptsTab; title: string }[] = [
  { id: 'journal', title: 'Журнал' },
  { id: 'attention', title: 'Требуют внимания' },
  { id: 'parked', title: 'Отложенные продажи' },
]

/** «Обмен с AQSI» продажи строки: что программа отправила и что AQSI ответил — отдельным окном. */
function ExchangeButton({ saleId, title, onOpen }: { saleId: string; title: string; onOpen: ((saleId: string) => void) | undefined }) {
  if (onOpen === undefined) return null
  return (
    <Button variant="ghost" icon={<ArrowLeftRight size={16} aria-hidden="true" />} onClick={() => onOpen(saleId)} aria-label={`Обмен с AQSI: продажа ${title}`}>
      Обмен с AQSI
    </Button>
  )
}

/**
 * «Отложенные продажи» (RP-FB35 K9): фоновая сверка оплаты и оплаченные продажи без чека. Не путать с
 * «Отложенными корзинами» окна РМК (товары без оплаты). «Вернуть на кассу» делает продажу снова продажей кассы; занята — слова службы.
 */
function ParkedPanel({
  vm,
  onRetry,
  onReturn,
  onExchange,
}: {
  vm: ParkedVm
  onRetry: () => void
  onReturn: ((saleId: string, version: number) => void) | undefined
  onExchange: ((saleId: string) => void) | undefined
}) {
  return (
    <section aria-label="Отложенные продажи">
      <p className="aqsi-journal__note">
        Продажи с фоновой сверкой оплаты или непробитым чеком. Состояние денег указано у каждой продажи. Корзины без оплаты — отдельно, в кнопке «Отложенные» РМК.
      </p>
      {vm.feedbackText !== null && vm.feedbackPhase !== 'done' && (
        <p className="aqsi-journal__note" data-tone="warning" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{vm.feedbackText}</span>
        </p>
      )}
      {vm.feedbackText !== null && vm.feedbackPhase === 'done' && (
        <p className="aqsi-journal__note" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>{vm.feedbackText}</span>
        </p>
      )}
      <QueryState query={vm.query} emptyText="Отложенных продаж нет" onRetry={onRetry} />
      {vm.rows.length > 0 && (
        <ul className="aqsi-journal__list" aria-label="Отложенные продажи">
          {vm.rows.map((row) => (
            <li key={row.saleId} className="aqsi-journal__item" data-tone={row.critical ? 'danger' : undefined}>
              <div className="aqsi-journal__item-main">
                <Clock size={16} aria-hidden="true" />
                <strong>{row.titleText}</strong>
                {onReturn !== undefined && (
                  <Button
                    variant="ghost"
                    disabled={vm.returnBlockedText !== null || vm.returning}
                    busy={vm.returning}
                    onClick={() => onReturn(row.saleId, row.version)}
                    aria-label={`Вернуть на кассу: продажа ${row.titleText}`}
                  >
                    Вернуть на кассу
                  </Button>
                )}
                <ExchangeButton saleId={row.saleId} title={row.titleText} onOpen={onExchange} />
              </div>
              <div className="aqsi-journal__meta">
                <span>{row.stateText}</span>
                {row.noteText !== null && <span>{row.noteText}</span>}
              </div>
              {row.lines.map((line) => (
                <p key={line} className="aqsi-journal__note" role="status">
                  {line}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
      {vm.returnBlockedText !== null && vm.rows.length > 0 && <p className="aqsi-journal__note">{vm.returnBlockedText}</p>}
    </section>
  )
}

/**
 * «Требуют внимания» (RP-FB35 K7): продажи ресурса с предупреждением службы — видны после F5 и на другом компьютере, в том числе
 * завершённые (`duplicate-suspected`: пробито два чека). Уровень — значок и слово («Важно» / «Внимание»), не цвет.
 */
function AttentionPanel({
  vm,
  onRetry,
  onOpenSale,
  onExchange,
}: {
  vm: AttentionVm
  onRetry: () => void
  onOpenSale: ((saleId: string) => void) | undefined
  onExchange: ((saleId: string) => void) | undefined
}) {
  return (
    <section aria-label="Требуют внимания">
      <QueryState query={vm.query} emptyText="Продаж, требующих внимания, нет" onRetry={onRetry} />
      {vm.rows.length > 0 && (
        <ul className="aqsi-journal__list" aria-label="Продажи, требующие внимания">
          {vm.rows.map((row) => (
            <li key={row.saleId} className="aqsi-journal__item" data-open={!row.finished} data-tone={row.critical ? 'danger' : 'warning'}>
              <div className="aqsi-journal__item-main">
                <AlertTriangle size={16} aria-hidden="true" />
                <strong>{row.titleText}</strong>
                <span>{row.deviceText}</span>
                <span>{row.finished ? 'продажа закрыта' : 'продажа не закрыта'}</span>
                {onOpenSale !== undefined && !row.finished && (
                  <Button variant="ghost" onClick={() => onOpenSale(row.saleId)} aria-label={`Открыть РМК: продажа ${row.titleText}`}>
                    Открыть РМК
                  </Button>
                )}
                <ExchangeButton saleId={row.saleId} title={row.titleText} onOpen={onExchange} />
              </div>
              <div className="aqsi-journal__meta">
                <span>{row.stateText}</span>
              </div>
              {row.lines.map((line) => (
                <p key={line} className="aqsi-journal__note" role="status">
                  {line}
                </p>
              ))}
              {row.latePayment === true && (
                <p className="aqsi-journal__note" role="status">
                  <Clock size={14} aria-hidden="true" /> Выбор «Оплаты не было» или «Оплата прошла — пробить чек» — в окне РМК этой кассы{onOpenSale !== undefined && !row.finished ? ': нажмите «Открыть РМК»' : ''}.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Вкладка «Чеки» кабинета AQSI (RP-FB74): находит день и чек, открывает детализацию и «Возврат». Слева — дерево «Где смотрим»
 * (кабинет → магазин → касса → смена), справа — дни, отбор по виду и кассиру и сами чеки: новые сверху, старые снизу. Без
 * выручки, итогов за период и графиков: всё это — во вкладке «Статистика по продажам» на тех же общих частях (`ReceiptsBrowser`). Экран получает vm и actions и больше ничего: ни store, ни HTTP, ни storage (R02). Состояние чека и сверки —
 * значок И слово, не цвет.
 */
export function ReceiptsScreen({ vm, actions, sectionTabs = false, receiptLines }: {
  vm: ReceiptsVm
  actions: ReceiptsActions
  sectionTabs?: boolean
  /** Товары чека под раскрытой строкой журнала (их читает binding). */
  receiptLines?: (receiptId: string) => ReactNode
}) {
  const { chooseTab } = actions
  // Вкладки — только если binding подключил их действие; иначе экран — журнал, как раньше. Смены — раздел «Смены», не вкладка чеков.
  const tabs = chooseTab !== undefined
  const attention = tabs && vm.tab === 'attention' && vm.attentionAllowed
  const parked = tabs && vm.tab === 'parked' && vm.attentionAllowed
  return (
    <section className="aqsi-journal" aria-labelledby="aqsi-receipts-title">
      <header className="aqsi-journal__head">
        <h2 id="aqsi-receipts-title">Чеки · {vm.title}</h2>
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          busy={vm.list.refreshing}
          onClick={actions.reload}
        >
          Обновить
        </Button>
      </header>

      {tabs && !sectionTabs && (
        <div className="aqsi-journal__actions" role="tablist" aria-label="Разделы чеков">
          {TABS.filter((tab) => (tab.id !== 'attention' && tab.id !== 'parked') || vm.attentionAllowed).map((tab) => (
            <Button
              key={tab.id}
              role="tab"
              aria-selected={vm.tab === tab.id}
              variant={vm.tab === tab.id ? 'primary' : 'ghost'}
              onClick={() => chooseTab(tab.id)}
            >
              {tab.id === 'attention' && vm.attention.count !== null && vm.attention.count > 0 && <AlertTriangle size={14} aria-hidden="true" />}
              {tab.title}
              {tab.id === 'attention' && vm.attention.count !== null && vm.attention.count > 0 ? ` (${vm.attention.count})` : ''}
              {tab.id === 'parked' && vm.parked.count !== null && vm.parked.count > 0 ? ` (${vm.parked.count})` : ''}
            </Button>
          ))}
        </div>
      )}

      {attention && <AttentionPanel vm={vm.attention} onRetry={actions.reload} onOpenSale={actions.openSale} onExchange={actions.openExchange} />}

      {parked && <ParkedPanel vm={vm.parked} onRetry={actions.reload} onReturn={actions.unparkSale} onExchange={actions.openExchange} />}

      {!attention && !parked && (
        <ReceiptsBrowser
          vm={vm}
          actions={actions}
          {...(receiptLines === undefined ? {} : { receiptLines })}
          multiDayText="Чеки показываются за один день. Чтобы увидеть чеки, выберите день: в списке дней ниже или на календаре. Выручка и итоги за период — во вкладке «Статистика по продажам»."
        />
      )}
    </section>
  )
}
